/**
 * Duelist tank particle effects — energy sword, meteor slash, tipper, iaijutsu dash.
 */

import type { ParticleCore } from '../types';
import { darken, lighten, warm } from '../color-utils';

/**
 * Shot 1: Energy Sword Sweep — white blade with red accents, drawn base-to-tip.
 * Follows aim angle — can slash horizontal, diagonal, or straight up.
 * @param baseX - Slash origin (tank position)
 * @param baseY - Slash origin Y
 * @param angle - Aim angle in radians
 * @param slashRange - Total blade length in px
 * @param chargeTier - 0=short, 1=mid, 2=long
 */
export function spawnDuelistSlash(
  core: ParticleCore,
  baseX: number, baseY: number,
  angle: number,
  slashRange: number, chargeTier: number,
): void {
  const now = Date.now();
  const cosA = Math.cos(angle);
  const sinA = Math.sin(angle);
  // Perpendicular direction for offset spawns (90° CCW from blade)
  const perpX = -sinA;
  const perpY = cosA;
  const tipX = baseX + cosA * slashRange;
  const tipY = baseY + sinA * slashRange;

  // Scale factors per charge tier
  const particleMult = chargeTier === 0 ? 0.6 : chargeTier === 1 ? 1.0 : 1.3;
  const showTipCyan = chargeTier >= 1;

  // ── Phase 0: Ignition (t=0ms) ──
  // Bright white-red muzzle burst at blade origin
  core.spawnParticle(baseX, baseY, 0xffffff, 0, 1.5, 0.06, 0.1, 1.5, 2.5, 0, 0.3, true, true, 0.9);
  core.spawnParticle(baseX, baseY, 0xff2266, 0, 1.0, 0.08, 0.12, 1.0, 2.0, 0, 0.4, false, false, 0.7);

  // Shockwave ring at origin
  core.spawnShockwave(baseX, baseY, 0xff2266, 40, 250, 2, 0.12);

  // Impact lines radiating forward along slash angle
  const lineCount = Math.round(5 * particleMult);
  for (let i = 0; i < lineCount; i++) {
    const spread = (Math.random() - 0.5) * 0.6; // narrow cone
    const lineAngle = angle + spread;
    const life = 0.1 + Math.random() * 0.05;
    core.impactLines.push({
      x: baseX, y: baseY, angle: lineAngle,
      innerRadius: 4,
      outerRadius: 25 + Math.random() * 15,
      expandSpeed: 200 + Math.random() * 60,
      color: Math.random() < 0.3 ? 0xffffff : 0xff2266,
      lineWidth: 2 + Math.random(),
      life, maxLife: life,
      freezeImmune: false,
    });
  }

  // ── Phase 1: Blade sweep (staggered across blade length) ──
  const sliceCount = 8;
  const sliceDelay = 15; // ms between slices
  for (let s = 0; s < sliceCount; s++) {
    const frac = (s + 1) / sliceCount;
    const delay = s * sliceDelay;
    // Position along the angled blade line
    const px = baseX + cosA * slashRange * frac;
    const py = baseY + sinA * slashRange * frac;

    const spawnSlice = () => {
      // White glow particles — blade body (ADD blend, soft)
      const glowCount = Math.round((3 + Math.random() * 2) * particleMult);
      for (let i = 0; i < glowCount; i++) {
        // Spread along blade direction + perpendicular
        const along = (Math.random() - 0.5) * 8;
        const perp = (Math.random() - 0.5) * 10;
        core.spawnParticle(
          px + cosA * along + perpX * perp,
          py + sinA * along + perpY * perp,
          0xffffff,
          0, 0.5,
          0.15, 0.25,
          0.6, 1.2,
          0, 0.4,
          true, false, // ADD blend, soft glow
          0.8,
        );
      }

      // Red accent particles offset perpendicular to blade
      const redCount = Math.round((2 + Math.random()) * particleMult);
      for (let i = 0; i < redCount; i++) {
        const side = (Math.random() < 0.5 ? -1 : 1) * (6 + Math.random() * 8);
        core.spawnParticle(
          px + perpX * side + cosA * (Math.random() - 0.5) * 6,
          py + perpY * side + sinA * (Math.random() - 0.5) * 6,
          Math.random() < 0.5 ? 0xff2266 : 0xcc1133,
          0, 0.3,
          0.12, 0.2,
          0.4, 0.8,
          0, 0.5,
          false, true, // NORMAL blend — keeps red true
          0.75,
        );
      }

      // Velocity-aligned streak in slash direction
      core.spawnParticleAtAngle(
        px, py,
        angle + (Math.random() - 0.5) * 0.15,
        6 + Math.random() * 4,
        0xffffff,
        0.08 + Math.random() * 0.05,
        0.3 + Math.random() * 0.2,
        0, 0.95,
        true, true,
        0.9,
        3, // stretch
      );
    };

    if (delay === 0) {
      spawnSlice();
    } else {
      core.pendingBlasts.push({ timestamp: now + delay, callback: spawnSlice });
    }
  }

  // ── Phase 2: Tip flash (t=120ms) ──
  if (showTipCyan) {
    core.pendingBlasts.push({
      timestamp: now + 120,
      callback: () => {
        // Cyan sparkle burst at tip
        const sparkCount = chargeTier === 2 ? 8 : 5;
        for (let i = 0; i < sparkCount; i++) {
          core.spawnParticle(
            tipX + (Math.random() - 0.5) * 6,
            tipY + (Math.random() - 0.5) * 6,
            Math.random() < 0.3 ? 0xffffff : 0x44ddff,
            1, 4,
            0.08, 0.15,
            0.3, 0.6,
            0, 0.92,
            true, true,
            0.9,
          );
        }
        // Thin cyan shockwave at tip
        core.spawnShockwave(tipX, tipY, 0x44ddff, 25, 200, 1.5, 0.1);
        // White impact line at tip extending forward
        const life = 0.1;
        core.impactLines.push({
          x: tipX, y: tipY, angle,
          innerRadius: 3,
          outerRadius: 20 + Math.random() * 10,
          expandSpeed: 180,
          color: 0xffffff,
          lineWidth: 2,
          life, maxLife: life,
          freezeImmune: false,
        });
      },
    });
  }

  // ── Phase 3: Dissipate (t=200-400ms) ──
  core.pendingBlasts.push({
    timestamp: now + 200,
    callback: () => {
      // Red embers drifting down from blade path
      const emberCount = Math.round(6 * particleMult);
      for (let i = 0; i < emberCount; i++) {
        const frac = Math.random();
        const ex = baseX + cosA * slashRange * frac;
        const ey = baseY + sinA * slashRange * frac;
        core.spawnParticle(
          ex + perpX * (Math.random() - 0.5) * 10,
          ey + perpY * (Math.random() - 0.5) * 10,
          Math.random() < 0.4 ? 0xff2266 : 0xcc1133,
          0.2, 1.0,
          0.25, 0.45,
          0.15, 0.3,
          0.08, // gravity — drift down
          0.97,
          false, true, // NORMAL
          0.6,
        );
      }
      // Faint white wisps along blade line
      for (let i = 0; i < 3; i++) {
        const frac = Math.random();
        const wx = baseX + cosA * slashRange * frac;
        const wy = baseY + sinA * slashRange * frac;
        core.spawnParticle(
          wx + perpX * (Math.random() - 0.5) * 8,
          wy + perpY * (Math.random() - 0.5) * 8,
          0xffffff,
          0.1, 0.5,
          0.2, 0.35,
          0.3, 0.6,
          -0.01, // float up slightly
          0.98,
          true, false, // ADD, soft
          0.3,
        );
      }
    },
  });
}

/**
 * Shot 2: Meteor Slash arc trail — blade ignites, heats up along arc.
 * @param baseX - Arc origin (tank X)
 * @param baseY - Arc origin (tank Y)
 * @param arcPoints - Array of {x, y, t} points along the arc (t: 0=top, 1=tip)
 * @param dirX - Facing direction (+1 right, -1 left)
 */
export function spawnDuelistArc(
  core: ParticleCore,
  baseX: number, baseY: number,
  arcPoints: { x: number; y: number; t: number }[],
  dirX: number,
): void {
  const now = Date.now();

  // ── Phase 0: Wind-up flash (t=0ms) ──
  // Orange-white burst above tank
  const topPt = arcPoints[0];
  core.spawnParticle(topPt.x, topPt.y, 0xffffff, 0, 1.5, 0.06, 0.1, 1.5, 2.5, 0, 0.35, true, true, 0.9);
  core.spawnParticle(topPt.x, topPt.y, 0xff8800, 0, 1.0, 0.08, 0.12, 1.0, 1.8, 0, 0.4, true, false, 0.7);
  // Small upward shockwave (blade igniting)
  core.spawnShockwave(topPt.x, topPt.y, 0xff8800, 30, 200, 2, 0.1);

  // ── Phase 1: Burning arc sweep (staggered along arc path) ──
  const stepCount = arcPoints.length;
  const totalArcTime = 200; // ms for full sweep
  for (let i = 0; i < stepCount; i++) {
    const pt = arcPoints[i];
    const delay = Math.round((pt.t) * totalArcTime);
    // Color gradient: orange at top → deep red at mid → bright crimson at tip
    const r = pt.t;
    let arcColor: number;
    if (r < 0.3) arcColor = 0xff8800; // orange
    else if (r < 0.6) arcColor = 0xdd3300; // deep red-orange
    else if (r < 0.85) arcColor = 0xcc1122; // deep red
    else arcColor = 0xff0044; // bright crimson at tip

    const spawnArcSlice = () => {
      // Scale up as blade descends (heats up)
      const heatScale = 0.6 + pt.t * 0.6; // 0.6 at top, 1.2 at tip

      // Glow particles in gradient color
      const glowCount = 2 + Math.round(pt.t); // more at bottom
      for (let g = 0; g < glowCount; g++) {
        core.spawnParticle(
          pt.x + (Math.random() - 0.5) * 8,
          pt.y + (Math.random() - 0.5) * 8,
          arcColor,
          0, 0.5,
          0.12, 0.22,
          0.5 * heatScale, 0.9 * heatScale,
          0, 0.4,
          true, false, // ADD, soft
          0.7 + pt.t * 0.2,
        );
      }

      // Velocity-aligned streak tangent to arc
      // Approximate tangent from adjacent points
      let tangentAngle: number;
      if (i < stepCount - 1) {
        const next = arcPoints[Math.min(i + 1, stepCount - 1)];
        tangentAngle = Math.atan2(next.y - pt.y, next.x - pt.x);
      } else {
        tangentAngle = dirX > 0 ? 0 : Math.PI; // horizontal at tip
      }
      core.spawnParticleAtAngle(
        pt.x, pt.y,
        tangentAngle + (Math.random() - 0.5) * 0.2,
        4 + Math.random() * 3,
        arcColor,
        0.1 + Math.random() * 0.06,
        0.25 * heatScale,
        0, 0.94,
        true, true,
        0.85,
        2.5, // stretch
      );

      // Occasional ember (every ~3rd point)
      if (Math.random() < 0.35) {
        core.spawnParticle(
          pt.x, pt.y,
          Math.random() < 0.5 ? 0xff6600 : arcColor,
          0.5, 2.0,
          0.2, 0.35,
          0.15, 0.25,
          0.1, // gravity — drifts down
          0.96,
          false, true, // NORMAL
          0.6,
        );
      }
    };

    if (delay <= 0) {
      spawnArcSlice();
    } else {
      core.pendingBlasts.push({ timestamp: now + delay, callback: spawnArcSlice });
    }
  }

  // ── Phase 2: Tip arrival (t=200ms) ──
  const tipPt = arcPoints[stepCount - 1];
  core.pendingBlasts.push({
    timestamp: now + totalArcTime,
    callback: () => {
      // Impact shockwave at arc endpoint
      core.spawnShockwave(tipPt.x, tipPt.y, 0xff2200, 50, 220, 2.5, 0.15);
      // Ground-level dust burst
      for (let i = 0; i < 6; i++) {
        const dustAngle = Math.PI + (Math.random() - 0.5) * Math.PI * 0.6; // upward spread
        core.spawnParticleAtAngle(
          tipPt.x + (Math.random() - 0.5) * 12,
          tipPt.y,
          -Math.PI / 2 + (Math.random() - 0.5) * Math.PI * 0.5,
          1.5 + Math.random() * 3,
          Math.random() < 0.3 ? 0x554433 : 0x443322,
          0.2 + Math.random() * 0.15,
          0.6 + Math.random() * 0.5,
          0.06, 0.96,
          false, false, // NORMAL, soft
          0.5,
        );
      }
      // Impact lines radiating from tip
      for (let i = 0; i < 7; i++) {
        const angle = (i / 7) * Math.PI * 2 + (Math.random() - 0.5) * 0.3;
        const life = 0.1 + Math.random() * 0.04;
        core.impactLines.push({
          x: tipPt.x, y: tipPt.y, angle,
          innerRadius: 3,
          outerRadius: 20 + Math.random() * 12,
          expandSpeed: 180 + Math.random() * 60,
          color: Math.random() < 0.4 ? 0xff4400 : 0xff0044,
          lineWidth: 2 + Math.random(),
          life, maxLife: life,
          freezeImmune: false,
        });
      }
    },
  });
}

/**
 * Tipper zone detonation — Jigglypuff rest-level impact.
 * Massive flash, eruption, celebration.
 */
export function spawnTipperDetonation(core: ParticleCore, x: number, y: number): void {
  const now = Date.now();

  // ── Phase 0: Impact freeze (t=0ms) ──
  // Hit cross — red-tinted, large
  for (let i = 0; i < 4; i++) {
    const angle = (i / 4) * Math.PI * 2 + Math.PI / 4;
    const life = 0.3;
    core.impactLines.push({
      x, y, angle,
      innerRadius: 0,
      outerRadius: 55,
      expandSpeed: 35,
      color: 0xff2222,
      lineWidth: 6,
      life, maxLife: life,
      freezeImmune: true,
    });
  }
  // Secondary cross
  for (let i = 0; i < 4; i++) {
    const angle = (i / 4) * Math.PI * 2;
    const life = 0.25;
    core.impactLines.push({
      x, y, angle,
      innerRadius: 0,
      outerRadius: 40,
      expandSpeed: 25,
      color: 0xcc0000,
      lineWidth: 4,
      life, maxLife: life,
      freezeImmune: true,
    });
  }

  // ── Phase 1: Core detonation (t=0-50ms) ──
  // White-hot core burst
  for (let i = 0; i < 24; i++) {
    core.spawnParticle(
      x + (Math.random() - 0.5) * 10,
      y + (Math.random() - 0.5) * 10,
      0xffffff,
      2, 8,
      0.08, 0.16,
      1.0, 2.5,
      0, 0.88,
      true, true, // ADD
      0.95,
    );
  }

  // Red ring burst — 16 particles at even angles
  for (let i = 0; i < 16; i++) {
    const angle = (i / 16) * Math.PI * 2;
    core.spawnParticleAtAngle(
      x, y,
      angle, 10 + Math.random() * 4,
      Math.random() < 0.3 ? 0xff2266 : 0xff0044,
      0.12 + Math.random() * 0.06,
      0.5 + Math.random() * 0.3,
      0, 0.93,
      true, true,
      0.9,
      2, // stretch
    );
  }

  // Concentric shockwaves — white inner, red outer
  core.spawnShockwave(x, y, 0xffffff, 60, 350, 3, 0.15);
  core.spawnShockwave(x, y, 0xff0044, 90, 280, 4, 0.2);

  // ── Phase 2: Eruption (t=80-150ms) ──
  core.pendingBlasts.push({
    timestamp: now + 80,
    callback: () => {
      // Dense red-crimson particle cloud
      for (let i = 0; i < 50; i++) {
        const r = Math.random();
        const color = r < 0.3 ? 0xff0044 : r < 0.6 ? 0xcc1133 : r < 0.8 ? 0xff2266 : 0xaa0022;
        const useAdd = Math.random() < 0.5;
        core.spawnParticle(
          x + (Math.random() - 0.5) * 16,
          y + (Math.random() - 0.5) * 16,
          color,
          1, 7,
          0.15, 0.3,
          0.4, 1.2,
          0.02, 0.94,
          useAdd, true,
          0.8,
        );
      }

      // Red impact lines — thick, expanding fast
      for (let i = 0; i < 10; i++) {
        const angle = (i / 10) * Math.PI * 2 + (Math.random() - 0.5) * 0.2;
        const life = 0.12 + Math.random() * 0.05;
        core.impactLines.push({
          x, y, angle,
          innerRadius: 5,
          outerRadius: 30 + Math.random() * 20,
          expandSpeed: 250 + Math.random() * 80,
          color: Math.random() < 0.3 ? 0xffffff : 0xff0044,
          lineWidth: 3 + Math.random(),
          life, maxLife: life,
          freezeImmune: false,
        });
      }

      // Red-orange ember rain (gravity, high upward velocity, arc back down)
      for (let i = 0; i < 16; i++) {
        const angle = -Math.PI / 2 + (Math.random() - 0.5) * Math.PI * 0.8;
        core.spawnParticleAtAngle(
          x + (Math.random() - 0.5) * 10,
          y,
          angle,
          5 + Math.random() * 8,
          Math.random() < 0.5 ? 0xff4400 : 0xff6600,
          0.3 + Math.random() * 0.25,
          0.2 + Math.random() * 0.15,
          0.12, // gravity — arcs back down
          0.97,
          false, true, // NORMAL
          0.7,
        );
      }
    },
  });

  // ── Phase 3: Celebration (t=200-500ms) ──
  // White sparkle twinkles around impact zone
  for (let i = 0; i < 8; i++) {
    const delay = 200 + Math.random() * 300;
    const sx = x + (Math.random() - 0.5) * 80;
    const sy = y + (Math.random() - 0.5) * 60;
    core.pendingBlasts.push({
      timestamp: now + delay,
      callback: () => {
        core.spawnParticle(
          sx, sy,
          0xffffff,
          0, 0.3,
          0.06, 0.12,
          0.3, 0.6,
          0, 0.9,
          true, true,
          0.9,
        );
      },
    });
  }

  // Faint red smoke drift (Sakurai baton-pass)
  core.pendingBlasts.push({
    timestamp: now + 250,
    callback: () => {
      for (let i = 0; i < 5; i++) {
        core.spawnParticle(
          x + (Math.random() - 0.5) * 30,
          y + (Math.random() - 0.5) * 20,
          Math.random() < 0.4 ? 0x331111 : 0x220011,
          0.3, 1.0,
          0.3, 0.5,
          1.0, 1.8,
          -0.01, // float up
          0.98,
          false, false, // NORMAL, soft
          0.35,
        );
      }
    },
  });
}

// === DUELIST DASH EFFECTS (Iaijutsu) ===

/**
 * Startup — converging energy particles rushing inward before dash.
 * "The breath before the strike."
 */
export function spawnDuelistStartup(core: ParticleCore, x: number, y: number, angle: number): void {
  // Converging energy from ring around body
  for (let i = 0; i < 8; i++) {
    const ringAngle = (i / 8) * Math.PI * 2;
    const dist = 40 + Math.random() * 20;
    const spawnX = x + Math.cos(ringAngle) * dist;
    const spawnY = y + Math.sin(ringAngle) * dist;
    const toCenter = Math.atan2(y - spawnY, x - spawnX);
    const speed = 10 + Math.random() * 6;
    const r = Math.random();
    const color = r < 0.4 ? 0xff2266 : r < 0.7 ? 0xffffff : 0xcc1133;
    core.spawnParticleAtAngle(
      spawnX, spawnY,
      toCenter, speed,
      color,
      0.06 + Math.random() * 0.03,
      0.2 + Math.random() * 0.15,
      0, 0.92,
      true, true,
      0.85,
      2.5, // stretch
    );
  }
  // Central compression point
  core.spawnParticle(x, y, 0xffffff, 0, 0, 0.05, 0.08, 0.5, 1.0, 0, 0.3, true, true, 0.9);
}

/**
 * Dash start — sonic boom shockwave + directional burst on first dash frame.
 */
export function spawnDuelistDashStart(core: ParticleCore, x: number, y: number, angle: number): void {
  // Sonic boom shockwave behind
  core.spawnShockwave(x, y, 0xff2266, 50, 300, 3, 0.18);
  core.spawnShockwave(x, y, 0xffffff, 30, 200, 2, 0.1);

  // Directional burst in dash direction
  const cos = Math.cos(angle);
  const sin = Math.sin(angle);
  for (let i = 0; i < 10; i++) {
    const spread = (Math.random() - 0.5) * 0.6;
    const particleAngle = angle + spread;
    const speed = 10 + Math.random() * 12;
    const r = Math.random();
    const color = r < 0.35 ? 0xff2266 : r < 0.6 ? 0xffffff : 0xcc1133;
    const off = 3 + Math.random() * 5;
    core.spawnParticleAtAngle(
      x + Math.cos(particleAngle) * off,
      y + Math.sin(particleAngle) * off,
      particleAngle, speed,
      color,
      0.08 + Math.random() * 0.06,
      0.2 + Math.random() * 0.25,
      0, 0.93,
      true, true,
      0.9,
      3 + Math.random() * 2, // long stretch
    );
  }

  // Impact lines radiating from start
  for (let i = 0; i < 5; i++) {
    const lineSpread = (Math.random() - 0.5) * 0.8;
    const lineAngle = angle + lineSpread;
    const life = 0.1 + Math.random() * 0.05;
    core.impactLines.push({
      x, y, angle: lineAngle,
      innerRadius: 4,
      outerRadius: 22 + Math.random() * 12,
      expandSpeed: 200 + Math.random() * 60,
      color: Math.random() < 0.3 ? 0xffffff : 0xff2266,
      lineWidth: 2 + Math.random(),
      life, maxLife: life,
      freezeImmune: false,
    });
  }
}

/**
 * Afterimage — crimson + white cluster at tank position during dash.
 * Spawned every 2 frames. Reads as "ghost images left behind."
 */
export function spawnDuelistAfterimage(core: ParticleCore, x: number, y: number, angle: number, color: number): void {
  // Crimson afterimage cluster
  for (let i = 0; i < 3; i++) {
    core.spawnParticle(
      x + (Math.random() - 0.5) * 12,
      y + (Math.random() - 0.5) * 12,
      Math.random() < 0.5 ? 0xff2266 : 0xcc1133,
      0, 0.3,
      0.15, 0.25,
      0.5, 1.0,
      0, 0.4,
      false, false,
      0.6,
    );
  }
  // White highlight
  core.spawnParticle(
    x + (Math.random() - 0.5) * 6,
    y + (Math.random() - 0.5) * 6,
    0xffffff,
    0, 0.2,
    0.1, 0.18,
    0.3, 0.6,
    0, 0.5,
    true, true,
    0.7,
  );
}

/**
 * Speed lines — stretched particles perpendicular to dash direction.
 * Spawned every frame during dash. Reads as "motion blur."
 */
export function spawnDuelistSpeedLines(core: ParticleCore, x: number, y: number, angle: number): void {
  const perpAngle = angle + Math.PI / 2;
  const side = Math.random() < 0.5 ? 1 : -1;
  const offset = 15 + Math.random() * 25;
  const sx = x + Math.cos(perpAngle) * offset * side;
  const sy = y + Math.sin(perpAngle) * offset * side;
  // Speed line moves opposite to dash direction (relative motion)
  const lineAngle = angle + Math.PI;
  core.spawnParticleAtAngle(
    sx, sy,
    lineAngle + (Math.random() - 0.5) * 0.2,
    4 + Math.random() * 6,
    Math.random() < 0.3 ? 0xffffff : 0xff2266,
    0.06 + Math.random() * 0.04,
    0.15 + Math.random() * 0.1,
    0, 0.95,
    true, true,
    0.6,
    4 + Math.random() * 3, // very stretched
  );
}

/**
 * Blade trail — elongated crimson particles hanging in the air along the dash path.
 * Spawned when dash hits. Persist through the dramatic pause.
 */
export function spawnDuelistBladeTrail(core: ParticleCore, startX: number, startY: number, endX: number, endY: number, angle: number): void {
  const dx = endX - startX;
  const dy = endY - startY;
  const dist = Math.sqrt(dx * dx + dy * dy);
  const perpX = -Math.sin(angle);
  const perpY = Math.cos(angle);

  // 12-15 elongated particles along the path
  const count = 12 + Math.round(Math.random() * 3);
  for (let i = 0; i < count; i++) {
    const frac = (i + 0.5) / count;
    const px = startX + dx * frac;
    const py = startY + dy * frac;
    const perp = (Math.random() - 0.5) * 8;
    const r = Math.random();
    const color = r < 0.5 ? 0xff2266 : r < 0.8 ? 0xcc1133 : 0xffffff;
    // Long life — persists through 40-frame pause (~0.67s)
    core.spawnParticle(
      px + perpX * perp,
      py + perpY * perp,
      color,
      0, 0.1,
      0.6, 0.8,
      0.3, 0.6,
      0, 0.2,
      r < 0.8, false, // mostly ADD blend for glow
      r < 0.8 ? 0.5 : 0.7,
    );
  }
}

/**
 * Slash resolve — blade trail shatters outward + massive detonation burst.
 * The cinematic payoff.
 */
export function spawnDuelistSlashResolve(
  core: ParticleCore,
  hitX: number, hitY: number,
  startX: number, startY: number,
  endX: number, endY: number,
  tier: number,
): void {
  const now = Date.now();

  // ── Hit cross at victim (freeze-immune) ──
  for (let i = 0; i < 4; i++) {
    const a = (i / 4) * Math.PI * 2 + Math.PI / 4;
    const life = 0.35;
    core.impactLines.push({
      x: hitX, y: hitY, angle: a,
      innerRadius: 0,
      outerRadius: tier === 2 ? 60 : 45,
      expandSpeed: 30,
      color: 0xff2266,
      lineWidth: tier === 2 ? 7 : 5,
      life, maxLife: life,
      freezeImmune: true,
    });
  }

  // ── Blade trail burst outward (shatter) ──
  const dx = endX - startX;
  const dy = endY - startY;
  const dist = Math.sqrt(dx * dx + dy * dy);
  for (let i = 0; i < 20; i++) {
    const frac = Math.random();
    const px = startX + dx * frac;
    const py = startY + dy * frac;
    const burstAngle = Math.random() * Math.PI * 2;
    const speed = 3 + Math.random() * 8;
    const r = Math.random();
    const color = r < 0.4 ? 0xff2266 : r < 0.7 ? 0xcc1133 : 0xffffff;
    core.spawnParticleAtAngle(
      px, py,
      burstAngle, speed,
      color,
      0.15 + Math.random() * 0.15,
      0.3 + Math.random() * 0.4,
      0.03, 0.94,
      true, true,
      0.8,
      2 + Math.random() * 2,
    );
  }

  // ── Core detonation at victim ──
  // White-hot burst
  for (let i = 0; i < 16; i++) {
    core.spawnParticle(
      hitX + (Math.random() - 0.5) * 10,
      hitY + (Math.random() - 0.5) * 10,
      0xffffff,
      2, 7,
      0.1, 0.2,
      0.8, 2.0,
      0, 0.88,
      true, true,
      0.95,
    );
  }

  // Crimson ring burst
  const ringCount = tier === 2 ? 16 : 12;
  for (let i = 0; i < ringCount; i++) {
    const a = (i / ringCount) * Math.PI * 2;
    core.spawnParticleAtAngle(
      hitX, hitY,
      a, 8 + Math.random() * 4,
      Math.random() < 0.3 ? 0xff2266 : 0xff0044,
      0.12 + Math.random() * 0.06,
      0.4 + Math.random() * 0.3,
      0, 0.93,
      true, true,
      0.9,
      2,
    );
  }

  // Shockwaves at victim
  core.spawnShockwave(hitX, hitY, 0xffffff, 50, 300, 3, 0.15);
  core.spawnShockwave(hitX, hitY, 0xff2266, 70, 250, 3.5, 0.2);

  // Impact lines at victim — thick, fast
  for (let i = 0; i < 8; i++) {
    const a = (i / 8) * Math.PI * 2 + (Math.random() - 0.5) * 0.2;
    const life = 0.12 + Math.random() * 0.05;
    core.impactLines.push({
      x: hitX, y: hitY, angle: a,
      innerRadius: 5,
      outerRadius: 28 + Math.random() * 18,
      expandSpeed: 250 + Math.random() * 80,
      color: Math.random() < 0.3 ? 0xffffff : 0xff2266,
      lineWidth: 3 + Math.random(),
      life, maxLife: life,
      freezeImmune: false,
    });
  }

  // ── Delayed ember rain (t=80ms) ──
  core.pendingBlasts.push({
    timestamp: now + 80,
    callback: () => {
      for (let i = 0; i < 12; i++) {
        const a = -Math.PI / 2 + (Math.random() - 0.5) * Math.PI * 0.8;
        core.spawnParticleAtAngle(
          hitX + (Math.random() - 0.5) * 10,
          hitY,
          a,
          4 + Math.random() * 7,
          Math.random() < 0.5 ? 0xff4400 : 0xff2266,
          0.25 + Math.random() * 0.2,
          0.15 + Math.random() * 0.1,
          0.1, 0.97,
          false, true,
          0.7,
        );
      }
    },
  });
}
